import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stack,
    Alert,
    CircularProgress,
    Box,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import client from '../api/client';

const emptyForm = { name: '', logo_filename: '' };

const AdminTeams = () => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editId, setEditId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchTeams = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await client.get('/teams/all');
            setTeams(res.data);
        } catch {
            setError('Nie udało się pobrać drużyn.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTeams();
    }, [fetchTeams]);

    const openCreate = () => {
        setForm(emptyForm);
        setEditId(null);
        setDialogOpen(true);
    };

    const openEdit = (team) => {
        setForm({ name: team.name, logo_filename: team.logo_filename || '' });
        setEditId(team.id);
        setDialogOpen(true);
    };

    const save = async () => {
        const payload = {
            name: form.name.trim(),
            logo_filename: form.logo_filename.trim() || null,
        };
        try {
            if (editId) {
                await client.put(`/teams/${editId}`, payload);
            } else {
                await client.post('/teams', payload);
            }
            setDialogOpen(false);
            fetchTeams();
        } catch {
            setError('Nie udało się zapisać drużyny.');
        }
    };

    const remove = async () => {
        try {
            await client.delete(`/teams/${confirmDelete.id}`);
            setConfirmDelete(null);
            fetchTeams();
        } catch {
            setError('Nie udało się usunąć drużyny.');
        }
    };

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">Drużyny</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Dodaj drużynę
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nazwa</TableCell>
                            <TableCell>Plik logo</TableCell>
                            <TableCell align="right">Akcje</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {teams.map((team) => (
                            <TableRow key={team.id}>
                                <TableCell>{team.name}</TableCell>
                                <TableCell>{team.logo_filename || '—'}</TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => openEdit(team)} aria-label="Edytuj">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => setConfirmDelete(team)}
                                        aria-label="Usuń"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {teams.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    Brak drużyn.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editId ? 'Edytuj drużynę' : 'Dodaj drużynę'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Nazwa"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required
                            fullWidth
                        />
                        <TextField
                            label="Plik logo (np. orzel-slupia.jpg)"
                            value={form.logo_filename}
                            onChange={(e) => setForm({ ...form, logo_filename: e.target.value })}
                            helperText="Nazwa pliku z katalogu /logos na stronie."
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Anuluj</Button>
                    <Button variant="contained" onClick={save} disabled={!form.name.trim()}>
                        Zapisz
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Usunąć drużynę?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Czy na pewno chcesz usunąć drużynę „{confirmDelete?.name}"? Tej operacji nie
                        można cofnąć.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Anuluj</Button>
                    <Button color="error" variant="contained" onClick={remove}>
                        Usuń
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminTeams;
